import React, { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import { getDatabaseHealth, getDatabaseServers, workspacesApi } from "../../actions";
import { brand, notice } from "../../theme/businessFlowsTheme";
import { canPlatform } from "../../utils/permissions";
import { ConfirmDialog, EmptyState, Hint, Refusal, Surface, dialogPaper, quietDanger, rowSx } from "./WorkspaceUi";

// What each readiness verdict means, as on the Databases screen.
const VERDICTS = {
  ready: { label: "Ready", tone: notice.good },
  readyWithWarnings: { label: "Ready, with notes", tone: notice.wait },
  needsAttention: { label: "Needs attention", tone: notice.fix },
  empty: { label: "Empty", tone: notice.quiet },
  unreachable: { label: "Offline", tone: notice.quiet },
  unknown: { label: "Couldn't check", tone: notice.quiet },
};

function Verdict({ verdict }) {
  if (verdict === undefined) return <Skeleton variant="rounded" width={88} height={22} />;
  const look = VERDICTS[verdict] || VERDICTS.unknown;
  return (
    <Chip
      size="small"
      label={look.label}
      sx={{ height: 22, fontWeight: 650, fontSize: "0.72rem", color: look.tone.fg, bgcolor: look.tone.bg, border: `1px solid ${look.tone.line}`, borderRadius: 1.5 }}
    />
  );
}

// How people reach one database: the views made for it, and how many people
// hold one. A member reaches a database **only** through a view they hold; a
// workspace admin reaches every database of the workspace by role.
//
// The product owner added a second database to a workspace and asked why a
// member still saw one. The model was right and the screen said nothing, so
// each row now says it: how many views and how many people, or - where there
// is no view - the one line that explains it, with the way to fix it beside.
export const reachText = (views) => {
  if (!views) return "";
  if (views.length === 0) return "No view yet, so only workspace admins can open it";
  const people = new Set(views.flatMap((view) => (view.usedBy || []).map((holder) => holder.userId || holder.email)));
  const v = `${views.length} ${views.length === 1 ? "view" : "views"}`;
  const p = `${people.size} ${people.size === 1 ? "person" : "people"}`;
  return `${v} · ${p}`;
};

// The views of this workspace that actually give one database - a view whose
// database was archived or removed gives nothing, and its own screen says so.
export const viewsForDatabase = (views, name) =>
  views === null ? null : views.filter((view) => view && view.grants && view.database === name);

// The databases this workspace owns. bpExperts adds and removes them;
// removing archives the link and leaves the database untouched.
export default function DatabasesTab({ workspace, user, isStaff, mayChange, notify, onChanged, onMakeView }) {
  const seesReadiness = canPlatform(user, "platform.view");
  const databases = useMemo(() => workspace.databases || [], [workspace.databases]);
  const [verdicts, setVerdicts] = useState({});
  const [adding, setAdding] = useState(false);
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState("");
  const [removing, setRemoving] = useState(null);
  // The workspace's views, so each row can say how people reach it. Read
  // from the same endpoint the Views tab uses; nothing new on the server.
  const [views, setViews] = useState(null);
  // The database just added: the next step is a view for it, offered here
  // rather than left for the admin to find.
  const [justAdded, setJustAdded] = useState(null);

  useEffect(() => {
    if (!seesReadiness) return undefined;
    let live = true;
    databases.forEach((database) =>
      getDatabaseHealth(database.name).then((res) => {
        if (live) setVerdicts((current) => ({ ...current, [database.name]: res?.success ? res.content?.verdict : "unknown" }));
      })
    );
    return () => {
      live = false;
    };
  }, [databases, seesReadiness]);

  useEffect(() => {
    let live = true;
    workspacesApi.views(workspace.id).then((res) => {
      if (live) setViews(res?.success ? res.content || [] : []);
    });
    return () => {
      live = false;
    };
  }, [workspace.id, databases]);

  useEffect(() => {
    if (!adding || options) return;
    getDatabaseServers().then((res) => {
      if (!res.success) {
        setAddError(res.msg || "Couldn't list the connected databases.");
        setOptions([]);
        return;
      }
      setOptions(
        (res.content || []).flatMap((server) =>
          (server.databases || []).filter((d) => d.key).map((d) => ({ key: d.key, label: d.label || d.key, server: server.name || "Main server" }))
        )
      );
    });
  }, [adding, options]);

  const owned = useMemo(() => new Set(databases.map((d) => d.name)), [databases]);
  const addable = (options || []).filter((option) => !owned.has(option.key));

  const openAdd = () => {
    setChoice(null);
    setAddError("");
    setOptions(null);
    setAdding(true);
  };

  const add = async () => {
    setBusy(true);
    setAddError("");
    const res = await workspacesApi.addDatabase(workspace.id, choice.key);
    setBusy(false);
    if (!res.success) {
      setAddError(res.msg || "Couldn't add the database.");
      return;
    }
    setAdding(false);
    notify(res.msg || `${choice.label} added to ${workspace.name}.`);
    setJustAdded({ name: choice.key, label: choice.label });
    onChanged();
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: "1 1 22rem", minWidth: "16rem" }}>
          {/* Groups decided this until 28 Sep 2026; views do now, and a
              reader of this tab should be told where to go next. */}
          {isStaff
            ? "People in this workspace reach a database through a view they hold. Views, and who holds each, are under Views."
            : "People in this workspace reach a database through a view they hold - see the Views tab. bpExperts adds and removes databases."}
        </Typography>
        {isStaff && databases.length > 0 && (
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openAdd}>
            Add database
          </Button>
        )}
      </Stack>

      {/* Adding a database is half the job: until a view is made for it, only
          workspace admins can open it. The next step is offered here rather
          than left for the admin to find - and nothing is created for them. */}
      {justAdded && onMakeView && mayChange && (
        <Hint
          title={`${justAdded.label} is in ${workspace.name}`}
          next="People reach a database through a view they hold. Workspace admins already open it."
          action={
            <Button size="small" variant="contained" onClick={() => onMakeView(justAdded.name)}>
              Make a view for it
            </Button>
          }
          onClose={() => setJustAdded(null)}
        >
          It has no view yet, so nobody but this workspace's admins can open it.
        </Hint>
      )}

      <Surface>
        {databases.length === 0 ? (
          <EmptyState
            icon={<StorageRoundedIcon />}
            title="No databases yet"
            action={
              isStaff ? (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openAdd}>
                  Add a database
                </Button>
              ) : null
            }
          >
            {isStaff ? "Add a database from any connected server." : "Ask bpExperts to add your first database."}
          </EmptyState>
        ) : (
          databases.map((database, index) => (
            <Stack
              key={database.name}
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ sm: "center" }}
              sx={rowSx(!index)}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: brand.canvas }}>
                  <StorageRoundedIcon fontSize="small" sx={{ color: brand.signalDeep }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{database.label}</Typography>
                  {/* How people reach it: the views made for it and how many
                      hold one, or the one line that says why a member can't
                      open it yet. */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", overflowWrap: "anywhere" }}>
                    {[database.serverName, reachText(viewsForDatabase(views, database.name))].filter(Boolean).join(" · ")}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
                {mayChange && onMakeView && (viewsForDatabase(views, database.name) || []).length === 0 && views !== null && (
                  <Button size="small" variant="outlined" startIcon={<AddRoundedIcon />} onClick={() => onMakeView(database.name)}>
                    Make a view
                  </Button>
                )}
                {seesReadiness && <Verdict verdict={verdicts[database.name]} />}
                {isStaff && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<LinkOffRoundedIcon />}
                    onClick={() => setRemoving(database)}
                    aria-label={`Remove ${database.label}`}
                    sx={quietDanger}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
            </Stack>
          ))
        )}
      </Surface>

      <Dialog open={adding} onClose={busy ? undefined : () => setAdding(false)} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add a database to {workspace.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Any database on a connected server. To connect a new server, use Databases in the console first.
          </Typography>
          <Autocomplete
            options={addable}
            loading={options === null}
            loadingText="Looking at the connected servers…"
            value={choice}
            onChange={(e, next) => setChoice(next)}
            groupBy={(option) => option.server}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.key === b.key}
            noOptionsText="Every connected database is already here."
            renderInput={(params) => <TextField {...params} label="Database" autoFocus />}
          />
          {addError && <Refusal sx={{ mt: 2 }}>{addError}</Refusal>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAdding(false)} disabled={busy}>
            Cancel
          </Button>
          <LoadingButton variant="contained" loading={busy} disabled={!choice} onClick={add}>
            Add
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(removing)}
        title={`Remove ${removing?.label} from ${workspace.name}?`}
        body="People in this workspace lose access to it at once, and the views made for it stop giving anything. The link is archived, not deleted, and the database and its content are untouched. Add it again to restore the link — and if the database itself has been archived, a bpExperts admin restores it under Databases → Archived."
        confirmLabel="Remove"
        onConfirm={async () => {
          const res = await workspacesApi.removeDatabase(workspace.id, removing.name);
          if (res.success) {
            notify(res.msg || `${removing.label} removed from this workspace.`);
            onChanged();
          }
          return res;
        }}
        onClose={() => setRemoving(null)}
      />
    </Stack>
  );
}
